/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 18-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Library-only helpers — per-trade/tenant color, template status, adopter counts.
 */

import { Trade, TemplateVersion } from '../types';
import { NT } from '../../NaomiTheme';

// [softBg, ink] pairs drawn from NAOMI's system theme accents (no brown/orange) — stable per-trade.
const TRADE_PALETTE: [string, string][] = [
  [NT.primarySoft, NT.primary],
  [NT.logicSoft, NT.logic],
  [NT.calcSoft, NT.calc],
  [NT.infoSoft, NT.info],
  [NT.successSoft, NT.success],
];
export function tradePalette(id: string): [string, string] {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) | 0;
  return TRADE_PALETTE[Math.abs(h) % TRADE_PALETTE.length];
}

// Tenant avatar colors — NAOMI system accents only (cool tones), stable per tenant id.
const TENANT_COLORS = [NT.primary, NT.logic, NT.calc, NT.info];
export function tenantColor(id: string): string {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) | 0;
  return TENANT_COLORS[Math.abs(h) % TENANT_COLORS.length];
}

export type TemplateStatus = 'draft' | 'published';

/**
 * Status chip for a template's ACTIVE (current) version — always 'published'. Published reads exclude
 * draft rows by design (a draft is a builder-session concept, status='draft'), so a version that
 * reaches the library is by definition published. Live drafts are surfaced separately by the amber
 * DraftChip / draft card, not here. Takes the version for call-site symmetry and future signals.
 */
export function templateStatus(_v?: TemplateVersion): TemplateStatus {
  return 'published';
}

/**
 * Adopter count for a trade's provider default = tenants that have adopted the trade (from the
 * provider tenant_trade_map, materialised into trade.tenants).
 */
export function providerAdopterCount(trade: Trade): number {
  return Object.keys(trade.tenants).length;
}

/** Global-default adopters = distinct tenants on trades that have no dedicated provider template. */
export function globalAdopterCount(trades: Trade[]): number {
  const seen = new Set<string>();
  trades.filter(t => !t.provider).forEach(t => Object.keys(t.tenants).forEach(tid => seen.add(tid)));
  return seen.size;
}

/**
 * Friendly relative timestamp for card footers ("just now" / "4 days ago" / "2 months ago").
 * Accepts a backend ISO string (updated_at); if the value is already a friendly label or unparseable,
 * it's returned as-is. Empty when absent.
 */
export function relativeWhen(when?: string | null): string {
  if (!when) return '';
  const t = Date.parse(when);
  if (Number.isNaN(t)) return when;
  const diff = Date.now() - t;
  const DAY = 86_400_000;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / 3_600_000)} hr ago`;
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)} day${Math.floor(diff / DAY) === 1 ? '' : 's'} ago`;
  if (diff < 30 * DAY) return `${Math.floor(diff / (7 * DAY))} week${Math.floor(diff / (7 * DAY)) === 1 ? '' : 's'} ago`;
  if (diff < 365 * DAY) return `${Math.floor(diff / (30 * DAY))} month${Math.floor(diff / (30 * DAY)) === 1 ? '' : 's'} ago`;
  return `${Math.floor(diff / (365 * DAY))} year${Math.floor(diff / (365 * DAY)) === 1 ? '' : 's'} ago`;
}

// tradesForTenant lives in the shared model — re-export so the library components import it from here.
export { tradesForTenant } from '../model';
